/* Employees API client — Phase 5 Bundle A
 *
 * The API returns flat shape:
 *   { id, userId, name, email, baseSalary, joiningDate, aadharNumber, ... }
 *
 * The admin UI expects nested shape (from the old SALES_TEAM mock):
 *   { id, name, email, phone, profile: { salary, joiningDate, aadharNumber, ... } }
 *
 * This module hides the difference. The bottom of this file exposes:
 *   - employeesApi.list()    → returns adapted records ready for UI
 *   - employeesApi.create()  → accepts UI-shaped records, sends API-shaped
 *   - employeesApi.update()  → ditto
 *   - employeesApi.archive() / restore()
 *
 * Adapters apiToUi() / uiToApi() are exported so AppDataContext can
 * use them too (when adapting an inline edit response).
 */
import { api } from "./api";

const BASE = "/employees";

// ─── Adapters ──────────────────────────────────────────────────────

export function employeeApiToUi(e) {
  if (!e) return e;
  return {
    id: e.id,
    userId: e.userId,
    name: e.name || "",
    email: e.email || "",
    phone: e.phone || "",
    active: e.active !== false,    // default true
    // users.active, distinct from employees.active above. Settings -> Users
    // toggles the former; archiving an employee toggles the latter, and they
    // drift. Payroll must honour both.
    userActive: e.userActive !== false,
    role: e.role || "employee",

    // Backward-compat: many admin screens read .profile.X
    profile: {
      fathersName: e.fathersName || "",
      dob: e.dob ? String(e.dob).slice(0, 10) : "",
      emergencyContact: e.emergencyContact || "",
      gender: e.gender || "",
      aadharNumber: e.aadharNumber || "",
      panNumber: e.panNumber || "",
      employeeCodeId: e.employeeCodeId || "",
      department: e.department || "Sales",
      designation: e.designation || "",
      joiningDate: e.joiningDate ? String(e.joiningDate).slice(0, 10) : "",
      // The old mock uses `salary`; we expose both for compat.
      salary: e.baseSalary != null ? Number(e.baseSalary) : 0,
      permanentAddress: e.permanentAddress || "",
      currentAddress: e.currentAddress || "",
    },

    // Targets — used by performance screens
    visitTarget: e.visitTarget ?? 0,
    tokenTarget: e.tokenTarget ?? 0,
    bookingTarget: e.bookingTarget ?? 0,

    // Mock had `active: 12` (lead count) — we don't compute that here.
    // Leave undefined; if a screen needs it, supply from leadsByEmployee.

    // Uploaded KYC documents (JSON) — read straight back for edit mode.
    documents: e.documents || {},

    createdAt: e.createdAt,
    updatedAt: e.updatedAt,
  };
}

export function employeeUiToApi(ui, opts = {}) {
  // Accept BOTH nested ui shape AND flat shape (from older callers).
  const p = ui.profile || {};
  const out = {
    // Auth/identity (only meaningful on create)
    ...(opts.includeAuth && { name: ui.name, email: ui.email, password: ui.password }),

    // Plain employee fields
    employeeCodeId: ui.employeeCodeId ?? p.employeeCodeId ?? null,
    department:     ui.department     ?? p.department     ?? null,
    designation:    ui.designation    ?? p.designation    ?? null,
    joiningDate:    ui.joiningDate    ?? p.joiningDate    ?? null,
    active:         ui.active,

    // Money
    baseSalary:     ui.baseSalary ?? p.salary ?? null,

    // Contact
    phone:          ui.phone          ?? null,
    fathersName:    ui.fathersName    ?? p.fathersName    ?? null,
    dob:            ui.dob            ?? p.dob            ?? null,
    gender:         ui.gender         ?? p.gender         ?? null,
    emergencyContact: ui.emergencyContact ?? p.emergencyContact ?? null,
    aadharNumber:   ui.aadharNumber   ?? p.aadharNumber   ?? null,
    panNumber:      ui.panNumber      ?? p.panNumber      ?? null,
    permanentAddress: ui.permanentAddress ?? p.permanentAddress ?? null,
    currentAddress:  ui.currentAddress  ?? p.currentAddress  ?? null,

    // Targets
    visitTarget:    ui.visitTarget   ?? p.visitTarget   ?? 0,
    tokenTarget:    ui.tokenTarget   ?? p.tokenTarget   ?? 0,
    bookingTarget:  ui.bookingTarget ?? p.bookingTarget ?? 0,

    // Updatable display name (Employee.name lives on User; service handles)
    name:           ui.name,

    // Uploaded KYC documents { aadhar, pan, education }
    documents:      ui.documents ?? null,
  };

  // DATA LOSS FIX -- drop keys the caller never mentioned.
  //
  // UpdateEmployeeDto is entirely @IsOptional, so an omitted field is left
  // untouched while an explicit null OVERWRITES it. Every `?? null` above
  // turned "the caller didn't supply this" into "clear it", and the
  // `?? 0` on the three targets turned it into "reset to zero".
  //
  // The consequences were real and silent, both returning 200:
  //   - AppDataContext:501 saves only visitTarget/tokenTarget/bookingTarget,
  //     which wiped salary, joining date, PAN, Aadhaar, phone, emergency
  //     contact and both addresses.
  //   - Saving the employee form, which carries no targets, reset all three
  //     performance targets to 0.
  //
  // A deliberate clear still works: the form then holds "" rather than
  // undefined, so the empty-string branch below converts it to null as
  // before. Only genuinely absent keys are dropped.
  //
  // Gated on `partial`, set only by update(). create() wants the defaults:
  // a new record with no salary genuinely has none. includeAuth is NOT the
  // signal -- both create and update pass it, since it gates the
  // name/email/password spread rather than the operation.
  //
  // One key does not match its source: baseSalary is built from
  // `ui.baseSalary ?? p.salary`, and AddSalesPersonWizard sends `salary`.
  // Checking out's own key name would delete the very field this fix
  // exists to protect, so it is resolved explicitly.
  if (opts.partial) {
    const supplied = (k) => {
      if (k === "baseSalary") return ui.baseSalary !== undefined || p.salary !== undefined;
      return ui[k] !== undefined || p[k] !== undefined;
    };
    for (const k of Object.keys(out)) {
      if (!supplied(k)) delete out[k];
    }
  }

  // Strip undefined / empty strings → null so the backend uses defaults
  const cleaned = {};
  for (const [k, v] of Object.entries(out)) {
    if (v === undefined) continue;
    if (v === "") { cleaned[k] = null; continue; }
    cleaned[k] = v;
  }
  return cleaned;
}

// ─── API methods ───────────────────────────────────────────────────

export const employeesApi = {
  /** List active employees (default). Pass {active: "all"} for everyone. */
  async list(opts = {}) {
    const qs = new URLSearchParams();
    if (opts.active === false) qs.set("active", "false");
    else if (opts.active !== "all") qs.set("active", "true");
    if (opts.q) qs.set("q", opts.q);
    qs.set("pageSize", "500");

    const res = await api.get(`${BASE}?${qs.toString()}`);
    return {
      items: (res.items || []).map(employeeApiToUi),
      total: res.total || 0,
    };
  },

  async getOne(id) {
    const res = await api.get(`${BASE}/${encodeURIComponent(id)}`);
    return employeeApiToUi(res);
  },

  async create(uiForm) {
    // Need auth fields on create
    const payload = employeeUiToApi(uiForm, { includeAuth: true });
    // password fallback if not provided
    if (!payload.password && uiForm.email) {
      payload.password = "TempPass" + Date.now();
    }
    const res = await api.post(BASE, payload);
    return employeeApiToUi(res);
  },

  async update(id, uiPatch) {
    // partial: true -- omit fields the caller never supplied rather than
    // sending null for them. See the DATA LOSS FIX note in employeeUiToApi.
    const payload = employeeUiToApi(uiPatch, { includeAuth: true, partial: true });
    // email CAN now be updated via this route (backend supports it)
    delete payload.password;
    const res = await api.patch(`${BASE}/${encodeURIComponent(id)}`, payload);
    return employeeApiToUi(res);
  },

  async archive(id) {
    const res = await api.post(`${BASE}/${encodeURIComponent(id)}/archive`, {});
    return employeeApiToUi(res);
  },

  async restore(id) {
    const res = await api.post(`${BASE}/${encodeURIComponent(id)}/restore`, {});
    return employeeApiToUi(res);
  },

  /** Phase 5 Bundle C — super_admin reset employee password.
   * Returns { tempPassword, userId, email }. UI shows + copies to clipboard. */
  async resetPassword(id) {
    return api.post(`${BASE}/${encodeURIComponent(id)}/reset-password`, {});
  },
};
